import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import OpenSeadragon from 'openseadragon';

import { ViewerContext } from './Viewer.hook';

type StylesProps = {
	open: boolean;
};

const useStyles = makeStyles(() => ({
	backdrop: (props: StylesProps) => ({
		position: 'fixed',
		top: 0,
		left: 0,
		width: '100vw',
		height: '100vh',
		backgroundColor: '#00000077',
		zIndex: 1500,
		display: props.open ? 'block' : 'none',
		'&.body': {
			overflow: 'hidden',
		},
	}),
	toolbar: {
		display: 'flex',
		backgroundColor: '#000000',
		height: '79px',
		justifyContent: 'center',
		flexWrap: 'wrap',
		padding: '5px',
	},
	toolbarAction: {
		color: '#ffffff',
	},
	viewer: {
		width: '100vw',
		height: 'calc(100vh - 79px)',
		zIndex: 1500,
	},
	paging: {
		color: '#ffffff',
		display: 'flex',
		alignItems: 'center',
	},
}));

const Viewer: FC = () => {
	const { t } = useTranslation('viewer');
	const { viewer, open, pages, currentPage, closeViewer } = useContext(
		ViewerContext,
	);

	const classes = useStyles({ open });

	const getZoom = useCallback(
		viewer =>
			viewer
				? viewer.viewport.viewportToImageZoom(viewer.viewport.getZoom())
				: 0,
		[],
	);

	const zoomIn = useCallback(
		(viewer?: OpenSeadragon.Viewer) => {
			if (getZoom(viewer) < 1.1) {
				viewer?.viewport.zoomBy(1.1);
			} else {
				viewer?.viewport.zoomTo(viewer?.viewport.imageToViewportZoom(1.1));
			}
		},
		[getZoom],
	);

	const zoomOut = useCallback(
		(viewer?: OpenSeadragon.Viewer) => {
			if (getZoom(viewer) > 0.15) {
				viewer?.viewport.zoomBy(0.9);
			}
		},
		[getZoom],
	);

	const rotateLeft = useCallback((viewer?: OpenSeadragon.Viewer) => {
		viewer?.viewport.setRotation(viewer?.viewport.getRotation() - 90);
	}, []);

	const rotateRight = useCallback((viewer?: OpenSeadragon.Viewer) => {
		viewer?.viewport.setRotation(viewer?.viewport.getRotation() + 90);
	}, []);

	const handlePrevPage = useCallback(
		(currentPage: number, viewer?: OpenSeadragon.Viewer, pages?: number) => {
			if (viewer) {
				let nextPage;

				if (currentPage === 0 && pages) {
					nextPage = pages - 1;
				} else {
					nextPage = currentPage - 1;
				}

				viewer.goToPage(nextPage);
			}
		},
		[],
	);

	const handleNextPage = useCallback(
		(currentPage: number, viewer?: OpenSeadragon.Viewer, pages?: number) => {
			if (viewer) {
				let nextPage;

				if (currentPage + 1 === pages) {
					nextPage = 0;
				} else {
					nextPage = currentPage + 1;
				}

				viewer.goToPage(nextPage);
			}
		},
		[],
	);

	const reset = useCallback(
		(viewer?: OpenSeadragon.Viewer) => viewer?.viewport.goHome(),
		[],
	);

	return (
		<Box display="flex" flexDirection="column">
			<div className={classes.backdrop}>
				<div className={classes.toolbar}>
					<IconButton
						id="zoom-in"
						className={classes.toolbarAction}
						onClick={() => zoomIn(viewer)}
					>
						<ZoomInIcon />
					</IconButton>
					<IconButton
						className={classes.toolbarAction}
						onClick={() => zoomOut(viewer)}
					>
						<ZoomOutIcon />
					</IconButton>
					<IconButton
						className={classes.toolbarAction}
						onClick={() => rotateLeft(viewer)}
					>
						<RotateLeftIcon />
					</IconButton>
					<IconButton
						className={classes.toolbarAction}
						onClick={() => rotateRight(viewer)}
					>
						<RotateRightIcon />
					</IconButton>
					<IconButton
						className={classes.toolbarAction}
						onClick={() => reset(viewer)}
					>
						<ZoomOutMapIcon />
					</IconButton>
					<IconButton
						className={classes.toolbarAction}
						onClick={() => handlePrevPage(currentPage, viewer, pages)}
					>
						<ArrowBackIcon />
					</IconButton>
					<IconButton
						className={classes.toolbarAction}
						onClick={() => handleNextPage(currentPage, viewer, pages)}
					>
						<ArrowForwardIcon />
					</IconButton>
					<IconButton className={classes.toolbarAction} onClick={closeViewer}>
						<CloseIcon />
					</IconButton>
					<div className={classes.paging}>
						{`${t('page')} ${currentPage + 1} ${t('from')} ${pages}`}
					</div>
				</div>
				<div id="image-viewer" className={classes.viewer} />
			</div>
		</Box>
	);
};

export default Viewer;
